// src/pages/insights/insights-index.tsx
//
// Page publique listant les "insights" (posts LinkedIn republiés sur le site).
// Utilise la table `articles` Supabase avec category = 'insights'.
// Bénéfice SEO double : chaque post devient une URL indexable + boost
// d'autorité du domaine sur les keywords RH / recrutement.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, ArrowRight, Calendar, Sparkles } from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import { supabase } from '../../lib/supabase';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

interface InsightArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  author: string | null;
  category: string | null;
  image_url: string | null;
  published_date: string | null;
  created_at: string;
  updated_at: string;
}

// — Helpers —

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function getPreview(article: InsightArticle, maxLen = 220): string {
  if (article.excerpt) return article.excerpt;
  // Strip HTML tags
  const text = article.content
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).replace(/[,. ]+$/, '') + '…';
}

export default function InsightsIndexPage() {
  const [articles, setArticles] = useState<InsightArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const { data, error: fetchErr } = await supabase
          .from('articles')
          .select(
            'id, slug, title, excerpt, content, author, category, image_url, published_date, created_at, updated_at',
          )
          .eq('published', true)
          .eq('category', 'insights')
          .order('created_at', { ascending: false });

        if (fetchErr) {
          console.error('[insights] fetch error:', fetchErr.message);
          setError('Impossible de charger les insights.');
        } else {
          setArticles((data as InsightArticle[]) ?? []);
        }
      } catch (e) {
        console.error('[insights] catch:', e);
        setError('Erreur réseau.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <Layout
      title="Insights RH · Posts LinkedIn de Gérald Gaillard | recruteurs.io"
      description="Insights LinkedIn de Gérald Gaillard (fondateur recruteurs.io, loocatme.fr, resoo.fr) sur le recrutement, l'onboarding, l'IA appliquée RH et le pilotage masse salariale. Republication des meilleurs posts sur le site, pour aller plus loin."
      keywords="insights rh, posts linkedin recrutement, gérald gaillard, recrutement marseille, onboarding, ia recrutement, recruteurs.io"
    >
      <WebPageSchema
        page={{
          title:
            'Insights RH · Posts LinkedIn de Gérald Gaillard | recruteurs.io',
          description:
            "Insights LinkedIn de Gérald Gaillard sur le recrutement, l'onboarding, l'IA appliquée RH et le pilotage masse salariale.",
          url: 'https://recruteurs.io/insights',
          lastReviewed: new Date().toISOString().slice(0, 10),
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Insights', url: 'https://recruteurs.io/insights' },
        ]}
      />

      {/* HERO */}
      <SectionBackground
        page="insights"
        section="i-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
      >
        <Section>
          <div className="max-w-3xl mx-auto text-center py-10">
            <span className={eyebrowClass}>
              <Linkedin className="w-3.5 h-3.5 mr-2 text-[#0077b5]" />
              Insights · Posts LinkedIn republiés
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold tracking-tight leading-[1.1] pb-1 mb-5">
              <span className="text-[#30475e]">Notre lecture du marché </span>
              <span className={gradientText}>RH, recrutement & IA.</span>
            </h1>
            <p className="text-lg text-neutral-700 leading-relaxed">
              Les posts LinkedIn de Gérald Gaillard, fondateur de
              recruteurs.io, loocatme.fr et resoo.fr. Recrutement,
              onboarding, IA appliquée, pilotage masse salariale —
              insights terrain, sans langue de bois.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* LISTING */}
      <SectionBackground page="insights" section="i-list" className="bg-white">
        <Section>
          <div className="max-w-5xl mx-auto">
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="rounded-2xl ring-1 ring-neutral-200 bg-white p-6 animate-pulse"
                  >
                    <div className="h-3 bg-neutral-100 rounded w-1/3 mb-3" />
                    <div className="h-5 bg-neutral-100 rounded w-3/4 mb-3" />
                    <div className="h-3 bg-neutral-100 rounded w-full mb-2" />
                    <div className="h-3 bg-neutral-100 rounded w-5/6" />
                  </div>
                ))}
              </div>
            )}

            {error && !loading && (
              <div className="rounded-2xl bg-red-50 ring-1 ring-red-200 p-6 text-center text-red-700">
                {error}
              </div>
            )}

            {!loading && !error && articles.length === 0 && (
              <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-white p-12 text-center">
                <Sparkles className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-sm text-neutral-500">
                  Les premiers insights arrivent bientôt.
                </p>
              </div>
            )}

            {!loading && articles.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/insights/${article.slug}`}
                    className="group flex flex-col rounded-2xl bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/40 hover:shadow-lg hover:-translate-y-0.5 transition-all p-6"
                  >
                    {/* Header card */}
                    <div className="flex items-center gap-2 mb-3 text-xs">
                      <Linkedin className="w-3.5 h-3.5 text-[#0077b5]" />
                      <span className="text-neutral-500">Insight LinkedIn</span>
                      <span className="text-neutral-300">·</span>
                      <span className="text-neutral-500 inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.published_date ?? article.created_at)}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-lg md:text-xl font-bold text-[#30475e] group-hover:text-[#d03484] transition-colors leading-snug mb-3">
                      {article.title}
                    </h2>

                    {/* Preview */}
                    <p className="text-sm text-neutral-600 leading-relaxed flex-1 line-clamp-4">
                      {getPreview(article)}
                    </p>

                    {/* Footer card */}
                    <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center justify-between">
                      <span className="text-xs text-neutral-500">
                        {article.author ?? 'Gérald Gaillard'}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#d03484] group-hover:translate-x-1 transition-transform">
                        Lire
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </Section>
      </SectionBackground>

      {/* CTA bottom — drive vers les ressources principales */}
      <SectionBackground
        page="insights"
        section="i-cta"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#30475e] mb-4 leading-tight">
              Pour aller plus loin,{' '}
              <span className={gradientText}>nos ressources gratuites.</span>
            </h2>
            <p className="text-base md:text-lg text-neutral-600 leading-relaxed mb-8">
              Les insights vous donnent la grille de lecture. Les ressources
              vous donnent les outils.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/ressources/articles"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
              >
                Voir tous les articles
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/ressources/calculateur-roi"
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 bg-white px-6 py-2.5 text-sm font-semibold text-[#30475e] hover:bg-[#fbfaf8] transition-all whitespace-nowrap"
              >
                Calculateur ROI
              </Link>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
